import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { FileDropZone } from "../FileDropZone";
import { AttachmentGallery } from "./AttachmentGallery";
import { fmtDateTime } from "./portalUtils";
import { PlusCircle, UserCircle, Key, CalendarBlank, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_META = {
  site_contact_name: { label: "Site contact", Icon: UserCircle },
  site_contact_phone: { label: "Site contact phone", Icon: Phone },
  access_notes: { label: "Access notes", Icon: Key },
  preferred_dates: { label: "Preferred dates", Icon: CalendarBlank },
};

/* Timestamped, append-only additions the client made after submitting the request. */
export function ClientUpdatesList({ updates = [], viewer = "client", testPrefix = "client-update" }) {
  if (!updates.length) return null;
  return (
    <div className="space-y-2 mt-3" data-testid={`${testPrefix}-list`}>
      {updates.map((u) => (
        <div key={u.id} className="rounded-md border border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20 p-3" data-testid={`${testPrefix}-${u.id}`}>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
            Client update · added by {u.user_name}{viewer === "admin" && u.client_name ? ` (${u.client_name})` : ""} · {fmtDateTime(u.created_at)}
            {viewer === "admin" && !u.seen_at && <span className="ml-2 rounded bg-emerald-600 text-white px-1.5 py-0.5 text-[9px]" data-testid={`${testPrefix}-new-${u.id}`}>NEW</span>}
          </p>
          {u.text && <p className="text-sm whitespace-pre-wrap">{u.text}</p>}
          {u.fields && Object.keys(u.fields).length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {Object.entries(u.fields).map(([k, v]) => { const m = FIELD_META[k] || { label: k, Icon: PlusCircle }; return (
                <li key={k} className="text-xs flex items-center gap-1.5"><m.Icon size={12} className="text-emerald-700 dark:text-emerald-400 shrink-0" /><span className="text-muted-foreground">{m.label}:</span> <span className="font-medium">{v}</span></li>
              ); })}
            </ul>
          )}
          {u.attachments?.length > 0 && <div className="mt-2"><AttachmentGallery attachments={u.attachments} testid={`${testPrefix}-files-${u.id}`} /></div>}
        </div>
      ))}
    </div>
  );
}

/* Portal: "Add details" — extra text, files and optional quick fields, appended to the request. */
export function AddDetailsButton({ requestId, onAdded, disabled }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ text: "", attachments: [], site_contact_name: "", site_contact_phone: "", access_notes: "", preferred_dates: "" });
  const [more, setMore] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hasContent = form.text.trim() || form.attachments.length || form.site_contact_name || form.site_contact_phone || form.access_notes || form.preferred_dates;

  const submit = async () => {
    setBusy(true);
    try {
      await API.post(`/client-hub/requests/${requestId}/details`, form);
      toast.success("Details added — our team has been notified");
      setOpen(false);
      setForm({ text: "", attachments: [], site_contact_name: "", site_contact_phone: "", access_notes: "", preferred_dates: "" });
      onAdded?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={disabled} data-testid="add-details-btn"><PlusCircle size={14} className="mr-1" /> Add details</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="add-details-dialog">
          <DialogHeader><DialogTitle>Add details to this request</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Anything you add is appended under your original request — nothing already sent is changed.</p>
          <Textarea rows={4} value={form.text} onChange={set("text")} placeholder="e.g. The leak has spread to the ceiling below — access is easiest before 10am." data-testid="add-details-text" />
          <FileDropZone value={form.attachments} onChange={(files) => setForm((f) => ({ ...f, attachments: files }))} folder="client-requests" compact />
          <button type="button" className="text-xs text-primary hover:underline text-left" onClick={() => setMore((m) => !m)} data-testid="add-details-more">
            {more ? "Hide" : "Update"} site contact, access or preferred dates
          </button>
          {more && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="add-details-fields">
              <div className="space-y-1"><Label className="text-xs">Site contact name</Label><Input value={form.site_contact_name} onChange={set("site_contact_name")} className="h-9" data-testid="add-details-contact-name" /></div>
              <div className="space-y-1"><Label className="text-xs">Site contact phone</Label><Input value={form.site_contact_phone} onChange={set("site_contact_phone")} className="h-9" data-testid="add-details-contact-phone" /></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Access notes</Label><Input value={form.access_notes} onChange={set("access_notes")} placeholder="Keys with reception, parking at rear…" className="h-9" data-testid="add-details-access" /></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Preferred dates / times</Label><Input value={form.preferred_dates} onChange={set("preferred_dates")} placeholder="Weekday mornings, not Fridays" className="h-9" data-testid="add-details-dates" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !hasContent} data-testid="add-details-submit">{busy ? "Sending…" : "Add details"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
